
import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import { AxiosError, AxiosResponse } from 'axios';
import { Chip, CircularProgress, Grid, TextField, Typography } from '@material-ui/core';

import { RevisionContext } from '../RevisionContext';
import { HomeContext } from '../../../Home/HomeContext';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { BatchCorrectionActions } from '../components/BatchCorrectionActions';
import { JournalistModel } from '../../../../Models/JournalistModel';
import JournalistService from '../../../../Services/JournalistService';
import { BatchCorrectionRemove } from '../components/BatchCorrectionRemove';
import ClassificationJournalistService from '../../../../Services/ClassificationJournalistService';
import { JournalistBatchModel, journalistBatchModelInitial } from '../../../../Models/JournalistBatchModel';
import { BatchCorrectionAction } from '../enum/BatchCorrectionAction';
import { BatchDto } from '../../../../Models/BatchDto';
import { BatchModel } from '../../../../Models/BatchModel';
import { BatchCorrectionContext } from '../BatchCorrectionContext';

export function JournalistBatchCorrection() {
    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);
    const batchCorrectionContext = React.useContext(BatchCorrectionContext);

    const [journalistListDelete, setJournalistListDelete] = React.useState<BatchDto[]>([]);
    const [journalistDeleteSelected, setJournalistDeleteSelected] = React.useState<BatchModel[]>([]);

    const [journalistListAdd, setJournalistListAdd] = React.useState<JournalistModel[]>([]);
    const [journalistAddSelected, setJournalistAddSelected] = React.useState<JournalistModel[]>([]);
    const [journalistSearch, setJournalistSearch] = React.useState<string>('');

    const [open, setOpen] = React.useState(false);
    const [journalistLoading, setJournalistLoading] = React.useState(false);

    const [journalistNotDefinedError, setJournalistNotDefinedError] = React.useState<boolean>(false);

    const [batchCorrectionAction, setBatchCorrectionAction] = React.useState<BatchCorrectionAction>(BatchCorrectionAction.ADD);

    React.useEffect(() => {
        const open = revisionContext.journalistBatchOpenDialog;
        setJournalistNotDefinedError(false);

        if (open && batchCorrectionAction === BatchCorrectionAction.REMOVE && journalistListDelete.length === 0)
            fetchCategoryJournalistList();
    }, [revisionContext.journalistBatchOpenDialog, batchCorrectionAction]);

    const fetchCategoryJournalistList = () => {
        const journalistBatch = journalistBatchModelInitial;
        journalistBatch.classificationsId = revisionContext.ids;

        ClassificationJournalistService.getJournalistBatch(journalistBatch)
            .then((res: AxiosResponse) => { setJournalistListDelete(res.data.journalistsDto); })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
    };

    const showSuccess = () => {
        handleClearModal();
        homeContext.showSuccess(`As alterações foram realizadas para todas as ${revisionContext.ids.length} classificações selecionadas com sucesso!`);
        batchCorrectionContext.setCallSearch(true);
    };

    const handleSaveButton = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            const journalistBatch = { ...journalistBatchModelInitial };
            journalistBatch.classificationsId = revisionContext.ids;

            if (batchCorrectionAction === BatchCorrectionAction.ADD) {
                journalistBatch.journalists = journalistAddSelected;
                updateJournalistBatch(journalistBatch);
            } else {
                journalistBatch.journalists = journalistDeleteSelected;
                deleteJournalistBatch(journalistBatch);
            }
        }
    };

    const updateJournalistBatch = (journalistBatch: JournalistBatchModel) => {
        ClassificationJournalistService.insertJournalistBatch(journalistBatch)
            .then(() => { showSuccess(); })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const deleteJournalistBatch = (journalistBatch: JournalistBatchModel) => {
        ClassificationJournalistService.deleteJournalistBatch(journalistBatch)
            .then(() => { showSuccess(); })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const handleClearModal = () => {
        revisionContext.setJournalistBatchOpenDialog(false);
        setJournalistListAdd([]);
        setJournalistAddSelected([]);
        setJournalistListDelete([]);
        setJournalistDeleteSelected([]);
        setBatchCorrectionAction(BatchCorrectionAction.ADD);
        setJournalistNotDefinedError(false);
    };

    const hasError = () => {
        let error = false;

        if (batchCorrectionAction === BatchCorrectionAction.ADD)
            error = (!journalistAddSelected || journalistAddSelected.length == 0);

        setJournalistNotDefinedError(error);
        return error;
    };

    const handleJournalistInput = (value: string) => {
        if (value.length > 2 && journalistSearch != value) {
            setJournalistLoading(true);
            setJournalistSearch(value);
            (async () => {
                JournalistService.getJournalistByQuery(value)
                    .then((res: AxiosResponse) => {
                        setJournalistLoading(false);
                        setJournalistListAdd(res.data);
                    })
                    .catch((error: AxiosError) => {
                        setJournalistLoading(false);
                        homeContext.showError('', error);
                    });
            })();
        }
        else
            setJournalistListAdd([]);
    };

    const getModal = () => {
        return (
            <Grid container spacing={3}>
                <BatchCorrectionActions
                    value={batchCorrectionAction}
                    setValue={setBatchCorrectionAction} />
                {batchCorrectionAction === BatchCorrectionAction.ADD && getJournalistAddInput()}
                {batchCorrectionAction === BatchCorrectionAction.REMOVE && getJournalistRemoveTags()}
            </Grid>
        );
    };

    const getJournalistAddInput = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id="news-journalist"
                    open={open}
                    onOpen={() => { setOpen(true); }}
                    onClose={() => { setOpen(false); }}
                    onInputChange={(_, newInputValue) => { handleJournalistInput(newInputValue); }}
                    value={journalistAddSelected}
                    onChange={(_, value: JournalistModel[]) => { setJournalistAddSelected(value); }}
                    getOptionSelected={(option, value) => option.name === value.name}
                    getOptionLabel={(option) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={journalistListAdd}
                    filterSelectedOptions
                    loading={journalistLoading}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Jornalista/autor"
                            placeholder="Digite o nome do jornalista para buscar"
                            variant="outlined"
                            error={journalistNotDefinedError && (!journalistAddSelected || journalistAddSelected.length == 0)}
                            helperText={journalistNotDefinedError && (!journalistAddSelected || journalistAddSelected.length == 0) ? 'Campo obrigatório' : ''}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {journalistLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }} />
                    )} />
            </Grid>
        );
    };

    const getJournalistRemoveTags = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant='subtitle1'>
                        Excluir jornalistas/autores
                    </Typography>
                    <Typography variant='caption'>
                        *Os jornalistas sem preenchimento não são comuns às classificações selecionadas
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <BatchCorrectionRemove
                        list={journalistListDelete}
                        setList={setJournalistListDelete}
                        removed={journalistDeleteSelected}
                        setRemoved={setJournalistDeleteSelected} />
                </Grid>
            </>
        );
    };

    return (
        <BasicConfirmDialog
            title={'Alteração de jornalistas'}
            description={'Exclua ou adicione jornalistas para as classificações selecionadas'}
            handleFirstButton={() => handleClearModal()}
            handleSecondButton={() => handleSaveButton()}
            textFirstButton='DESCARTAR'
            textSecondButton='SALVAR'
            open={revisionContext.journalistBatchOpenDialog}
            children={getModal()}
            cssProperties={{
                minWidth: '800px',
                maxWidth: '800px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} />
    );
}